import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Product } from 'services';
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import Currency from "utils/currency";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 450,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

const schema = {
  price_currency: {
    presence: { allowEmpty: false, message: "is required" },
  },
  price: {
    presence: { allowEmpty: false, message: "is required" },
    numericality: { greaterThanOrEqualTo: 0 }
  },
  name_en: {
    presence: { allowEmpty: false, message: "is required" },
  },
  code: {
    presence: { allowEmpty: false, message: "is required" },
  },
  sku: {
    presence: { allowEmpty: false, message: "is required" },
  },
  type: {
    presence: { allowEmpty: false, message: "is required" },
  },
};

const INIT_VALUES = {
  name_en: "",
  price: "",
  price_currency: "SGD",
  code: "",
  sku: "",
  type: "pass_purchase"
};

const CreateModal = props => {
  let { onCreate, open, onClose, className, ...rest } = props;

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const runAsyncBlock = useErrorCatcher();

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...INIT_VALUES },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    setFormState({
      isValid: false,
      values: { ...INIT_VALUES },
      touched: {},
      errors: {}
    });
  }, [open]);

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  if (!open) {
    return null;
  }


  const handleSubmit = event => {
    event.preventDefault();

    setFormState(formState => ({
      ...formState,
      touched: {
        // to show error message
        name_en: true,
        code: true,
        sku: true,
        price: true,
        type: true
      }
    }))
    if (Object.keys(formState.errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      const { name_en, code, sku, price, price_currency, type } = formState.values;
      await Product.create({ name_en, code, sku, price: Currency.uncompress(price, price_currency), price_currency, type });
      onCreate();
      onClose();
    }).finally(() => {
      setLoading(false);
    });

  }

  const handleFieldChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  return (
    <Modal open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Add Product
            </Typography>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <CustTextField name="name_en" label="Name" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="code" label="Code" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField name="sku" label="SKU" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField type="number" name="price" label="Price" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField dropdown name="price_currency" label="Currency" options={[{ value: "SGD", label: "SGD" }]} formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              <Grid item md={6} xs={6}>
                <CustTextField dropdown name="type" label="Type" options={[{ value: "pass_purchase", label: "Passes Purchase" }]} formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleSubmit} variant="contained" disabled={loading} >Create</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}



const CustTextField = props => {
  let { label, name, formState, handleFieldChange, type, dropdown, options, required } = props;
  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <TextField
      fullWidth
      label={label}
      helperText={hasError(name) ? formState.errors[name][0] : null}
      name={name}
      onChange={handleFieldChange}
      value={formState.values[name]}
      error={hasError(name)}
      variant="outlined"
      type={type}
      select={dropdown}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: dropdown }}
      required={required}
    >
      {options && options.length > 0 && options.map((opt, index) => (
        <option key={index} value={opt.value}>{opt.label}</option>
      ))
      }
    </TextField>
  );
}


export default CreateModal;